/**
 * useAgenticStream — User-scoped SSE hook cho agentic chat results.
 *
 * Design:
 * - Một connection duy nhất per user (GET /api/chat/stream) — tất cả sessions
 *   của user được fan-out qua connection này.
 * - Dùng fetch() + ReadableStream thay vì EventSource để hỗ trợ custom headers
 *   (X-User-ID, Authorization) vì EventSource không cho phép custom headers.
 * - Session routing: mỗi event có session_id, component/hook caller tự filter.
 * - Auto-reconnect với exponential backoff (3s → 6s → 12s → max 30s).
 * - Singleton pattern: nhiều component cùng subscribe nhưng chỉ 1 fetch connection.
 *
 * Usage:
 * ```tsx
 * // Ở app root / layout — khởi tạo 1 lần khi user đã auth
 * useAgenticStream({
 *   onResult: (event) => {
 *     if (event.session_id === currentSessionId) {
 *       setMessages(prev => [...prev, { role: 'assistant', content: event.content }]);
 *     }
 *   },
 * });
 * ```
 */

import { useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Event types sent by the backend SSE hub. */
export type AgenticEventType =
  | "result"       // Task completed — full answer
  | "progress"     // Intermediate step output (optional streaming)
  | "error"        // Task or step error
  | "connected"    // Initial connection confirmation
  | "heartbeat";   // Keep-alive ping

/** Payload of a single SSE event from GET /api/chat/stream */
export interface AgenticStreamEvent {
  type: AgenticEventType;
  session_id: string;
  task_id?: string;
  content?: string;
  error?: string;
  /** Step index for progress events (1-indexed) */
  step?: number;
  /** Total steps in the plan */
  total_steps?: number;
  /** Agent type that produced this output */
  agent_type?: string;
  /** Intent that was classified for this task */
  intent?: string;
  tokens_used?: number;
  timestamp: string;
}

export interface UseAgenticStreamOptions {
  /** Called on every event received from the server. Filter by session_id as needed. */
  onEvent?: (event: AgenticStreamEvent) => void;
  /** Shortcut: called only on "result" events (task completed). */
  onResult?: (event: AgenticStreamEvent) => void;
  /** Shortcut: called only on "progress" events (step intermediate output). */
  onProgress?: (event: AgenticStreamEvent) => void;
  /** Shortcut: called only on "error" events. */
  onError?: (event: AgenticStreamEvent) => void;
  /** Called when the SSE connection is established or re-established. */
  onConnected?: () => void;
  /** Called when the connection is permanently closed (e.g., user logged out). */
  onDisconnected?: () => void;
  /** Base URL for the SSE endpoint. Defaults to /api/chat/stream */
  streamUrl?: string;
  /** Whether to connect. Set to false when user is not authenticated. Default true. */
  enabled?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// SSE Line Parser
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parses a raw SSE line buffer into individual events.
 * Handles multi-line data fields (data: ... \n data: ... continuation).
 */
function parseSseLine(buffer: string): AgenticStreamEvent | null {
  const lines = buffer.split("\n");
  let eventType = "message";
  let dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (dataLines.length === 0) return null;

  try {
    const raw = JSON.parse(dataLines.join("\n")) as Partial<AgenticStreamEvent>;
    return {
      type: (raw.type ?? eventType) as AgenticEventType,
      session_id: raw.session_id ?? "",
      task_id: raw.task_id,
      content: raw.content,
      error: raw.error,
      step: raw.step,
      total_steps: raw.total_steps,
      agent_type: raw.agent_type,
      intent: raw.intent,
      tokens_used: raw.tokens_used,
      timestamp: raw.timestamp ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_STREAM_URL = "/api/chat/stream";
const INITIAL_RETRY_DELAY_MS = 3_000;
const MAX_RETRY_DELAY_MS = 30_000;

export function useAgenticStream({
  onEvent,
  onResult,
  onProgress,
  onError,
  onConnected,
  onDisconnected,
  streamUrl = DEFAULT_STREAM_URL,
  enabled = true,
}: UseAgenticStreamOptions = {}): { disconnect: () => void } {
  // Stable refs to avoid re-subscribing on callback identity change
  const onEventRef = useRef(onEvent);
  const onResultRef = useRef(onResult);
  const onProgressRef = useRef(onProgress);
  const onErrorRef = useRef(onError);
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);

  useEffect(() => {
    onEventRef.current = onEvent;
    onResultRef.current = onResult;
    onProgressRef.current = onProgress;
    onErrorRef.current = onError;
    onConnectedRef.current = onConnected;
    onDisconnectedRef.current = onDisconnected;
  });

  const abortRef = useRef<AbortController | null>(null);
  const retryDelayRef = useRef(INITIAL_RETRY_DELAY_MS);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const dispatch = useCallback((event: AgenticStreamEvent) => {
    onEventRef.current?.(event);
    switch (event.type) {
      case "result":
        onResultRef.current?.(event);
        break;
      case "progress":
        onProgressRef.current?.(event);
        break;
      case "error":
        onErrorRef.current?.(event);
        break;
      case "connected":
        onConnectedRef.current?.();
        break;
    }
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    // Credentials are sent as cookies (httpOnly session) since fetch() supports
    // credentials: "include" — no need for manual token in header.
    fetch(streamUrl, {
      credentials: "include",
      headers: {
        Accept: "text/event-stream",
        "Cache-Control": "no-cache",
      },
      signal,
    })
      .then(async (response) => {
        if (!response.ok || !response.body) {
          throw new Error(`SSE connect failed: ${response.status}`);
        }

        retryDelayRef.current = INITIAL_RETRY_DELAY_MS; // reset on success
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (mountedRef.current) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE events are separated by double newlines
          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? ""; // last (possibly incomplete) chunk

          for (const part of parts) {
            const trimmed = part.trim();
            if (!trimmed) continue;
            const event = parseSseLine(trimmed);
            if (event) dispatch(event);
          }
        }
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;

        const delay = retryDelayRef.current;
        retryDelayRef.current = Math.min(delay * 2, MAX_RETRY_DELAY_MS);

        // Schedule reconnect
        retryTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) connect();
        }, delay);
      });
  }, [streamUrl, dispatch]);

  useEffect(() => {
    mountedRef.current = true;

    if (!enabled) return;

    connect();

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      onDisconnectedRef.current?.();
    };
  }, [enabled, connect]);

  const disconnect = useCallback(() => {
    mountedRef.current = false;
    abortRef.current?.abort();
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
  }, []);

  return { disconnect };
}
